import {
  genericForwardRefMemo,
  genericMemo,
} from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up_filled.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { ForwardedRef, useState } from "react";
import { Flex } from "../../flex";
import { Icon } from "../../icon";
import { TextSizeValue } from "../../theme/typography";
import { DescriptionText, LabelText } from "../common/descriptors";
import {
  BadgeRedoListItem,
  RedoListItem,
  SectionRedoListItem,
} from "../list/redo-list";
import { dropdownSizeToListSize } from "../list/redo-list-dropdown";
import { RedoMultiselectDropdown } from "../list/redo-multiselect-dropdown";
import {
  BadgeRedoListItem as BadgeRedoListItemComponent,
  TextRedoListItem,
} from "../list/standard-redo-list-items";
import { RedoSpinner } from "../loading/redo-spinner";
import * as redoDropdownInputCss from "./redo-dropdown-input.module.css";
import {
  RedoDropdownInputSize,
  RedoDropdownInputState,
} from "./redo-single-select-dropdown-input";

export type ValidBadgeRedoListItems<T> =
  | BadgeRedoListItem<T>
  | SectionRedoListItem<T>;

/**
 * @param searchSubmitted - use this to create a tag if that is applicable to your use case
 * This could be reworked into a 'create tag strategy' that could either be
 * search submission or creates a 'create tag' list item.
 */
export interface RedoTagsDropdownInputProps<T> {
  autoFocus?: boolean;
  size?: RedoDropdownInputSize;
  options: ValidBadgeRedoListItems<T>[];
  selectedOptions: BadgeRedoListItem<T>[];
  setSelectedOptions(items: BadgeRedoListItem<T>[]): void;
  searchSubmitted?: (searchString: string) => void;
  disabled?: boolean;
  className?: string;
  fitToAnchor?: boolean;
  label?: string;
  placeholder?: string;
  description?: string;
  searchString?: string;
  setSearchString?: (searchText: string) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  flexGrow?: boolean;
  onDropdownOpenChange?: ((newOpenValue: boolean) => void) | undefined;
  state?: RedoDropdownInputState;
  onBlur?: () => void;
  singleRow?: boolean;
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
  disabledTooltips?: Record<string | number, string>;
}

export const RedoTagsDropdownInput = genericMemo(function RedoTagsDropdownInput<
  T,
>({
  autoFocus,
  options,
  selectedOptions = [],
  setSelectedOptions,
  searchSubmitted,
  size = "md",
  disabled = false,
  className,
  fitToAnchor = true,
  label,
  placeholder,
  description,
  searchString,
  setSearchString,
  searchPlaceholder,
  loading,
  flexGrow = true,
  onDropdownOpenChange,
  state = "default",
  onBlur,
  singleRow = false,
  disabledItems,
  disabledTooltips,
}: RedoTagsDropdownInputProps<T>) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [dropdownOpen, _setDropdownOpen] = useState(autoFocus || false);
  const setDropdownOpen = useHandler((open: boolean) => {
    onDropdownOpenChange?.(open);
    _setDropdownOpen(open);
    if (!open) {
      onBlur?.();
    }
  });
  const toggleDropdownOpen = useHandler(() => {
    _setDropdownOpen((prev) => {
      const newOpen = !prev;
      onDropdownOpenChange?.(newOpen);
      if (!newOpen) {
        onBlur?.();
      }
      return newOpen;
    });
  });

  const descriptorFontSize = sizeToDescriptorTextProps[size];

  return (
    <>
      <Flex dir="column" gap="sm" grow={flexGrow ? 1 : undefined}>
        <LabelText label={label} size={descriptorFontSize} />
        <RedoTagsDropdownInputButton
          className={className}
          disabled={disabled}
          disabledItems={disabledItems}
          isDropdownOpen={dropdownOpen}
          loading={loading}
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdownOpen();
          }}
          placeholder={placeholder}
          ref={setDropdownButtonRef}
          removeItem={(item) => {
            if (isItemDisabled(item.id, disabledItems)) {
              return;
            }
            setSelectedOptions(
              selectedOptions.filter(
                (selectedItem) => selectedItem.id !== item.id,
              ),
            );
          }}
          selectedItems={selectedOptions}
          singleRow={singleRow}
          size={size}
          state={state}
        />
        <DescriptionText
          description={description}
          error={state === "error"}
          size={descriptorFontSize}
        />
      </Flex>
      <RedoMultiselectDropdown
        disabledItems={disabledItems}
        disabledTooltips={disabledTooltips}
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor={fitToAnchor}
        items={options}
        itemsLoading={loading}
        searchPlaceholder={searchPlaceholder ?? placeholder}
        searchString={searchString}
        searchSubmitted={searchSubmitted}
        selectedItems={selectedOptions}
        selectionVariant="checkmark"
        setDropdownOpen={setDropdownOpen}
        setSearchString={setSearchString}
        setSelectedItems={
          setSelectedOptions as (items: RedoListItem<T>[]) => void
        }
        size={size}
      />
    </>
  );
});

function isItemDisabled(
  itemId: string | number | symbol,
  disabledItems?: (string | number | symbol)[] | (string | number | symbol),
): boolean {
  if (!disabledItems) {
    return false;
  }
  return Array.isArray(disabledItems)
    ? disabledItems.includes(itemId)
    : disabledItems === itemId;
}

interface RedoTagsDropdownInputButtonProps<T> {
  size?: RedoDropdownInputSize;
  isDropdownOpen: boolean;
  selectedItems?: BadgeRedoListItem<T>[];
  removeItem?: (item: BadgeRedoListItem<T>) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  loading?: boolean;
  ref?: ForwardedRef<HTMLButtonElement>;
  state?: RedoDropdownInputState;
  singleRow?: boolean;
  disabledItems?: (string | number | symbol)[] | (string | number | symbol);
}

export const RedoTagsDropdownInputButton = genericForwardRefMemo(
  function RedoTagsDropdownInputButton<T>(
    {
      selectedItems,
      removeItem,
      isDropdownOpen,
      size = "md",
      disabled = false,
      placeholder,
      className,
      onClick,
      loading = false,
      state = "default",
      singleRow = false,
      disabledItems,
    }: RedoTagsDropdownInputButtonProps<T>,
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    // Calculate visible items for single row mode
    const visibleItems =
      singleRow && selectedItems && selectedItems.length > 3
        ? selectedItems.slice(0, 3)
        : selectedItems;
    const hiddenCount =
      singleRow && selectedItems
        ? selectedItems.length - (visibleItems?.length || 0)
        : 0;

    return (
      <button
        className={classNames(
          className,
          redoDropdownInputCss.tagsInput,
          dropdownInputSizeToSizeClass[size],
          isDropdownOpen && redoDropdownInputCss.open,
          disabled && redoDropdownInputCss.disabled,
          state === "error" && redoDropdownInputCss.error,
        )}
        disabled={disabled}
        onClick={onClick}
        ref={ref}
        type="button"
      >
        <Flex
          className={classNames(
            redoDropdownInputCss.tagsContainer,
            singleRow && redoDropdownInputCss.singleRowInput,
          )}
          wrap={singleRow ? "nowrap" : "wrap"}
        >
          {visibleItems && visibleItems.length ? (
            <>
              {visibleItems.map((selectedItem) => (
                <div
                  className={redoDropdownInputCss.tag}
                  key={getKey(selectedItem.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isItemDisabled(selectedItem.id, disabledItems)) {
                      return;
                    }
                    removeItem?.(selectedItem);
                  }}
                >
                  <BadgeRedoListItemComponent
                    badge={{
                      ...selectedItem.badge,
                      iconTrailing: [
                        // Keep existing icons if any, but add X icon
                        ...[selectedItem.badge.iconTrailing ?? []].flat(),
                        ...(removeItem &&
                        !isItemDisabled(selectedItem.id, disabledItems)
                          ? [{ Icon: XIcon, type: "icon" } as const]
                          : []),
                      ],
                    }}
                    size={dropdownSizeToListSize[size]}
                    useWrapper={false}
                  />
                </div>
              ))}
              {hiddenCount > 0 && (
                <div className={redoDropdownInputCss.tag}>
                  <BadgeRedoListItemComponent
                    badge={{ text: `+${hiddenCount} more`, color: "gray" }}
                    size={dropdownSizeToListSize[size]}
                    useWrapper={false}
                  />
                </div>
              )}
            </>
          ) : (
            <TextRedoListItem
              placeholder
              size={dropdownSizeToListSize[size]}
              text={placeholder || ""}
              useWrapper={false}
            />
          )}
        </Flex>
        {loading ? (
          <RedoSpinner
            height={dropdownInputSizeToSpinnerBox[size]}
            size="xs"
            width={dropdownInputSizeToSpinnerBox[size]}
          />
        ) : (
          <Icon
            arbiterIconSvg={isDropdownOpen ? ChevronUpIcon : ChevronDownIcon}
            className={redoDropdownInputCss.chevron}
            color="ghost"
          />
        )}
      </button>
    );
  },
);

/** Matches `.chevron` width/height in redo-dropdown-input.module.css per size. */
const dropdownInputSizeToSpinnerBox: Record<RedoDropdownInputSize, number> = {
  xxs: 12,
  xs: 16,
  sm: 16,
  md: 20,
  lg: 24,
};

const dropdownInputSizeToSizeClass: Record<RedoDropdownInputSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  xxs: redoDropdownInputCss.xxSmall,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  xs: redoDropdownInputCss.xSmall,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  sm: redoDropdownInputCss.small,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  md: redoDropdownInputCss.regular,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  lg: redoDropdownInputCss.large,
};

const sizeToDescriptorTextProps: Record<RedoDropdownInputSize, TextSizeValue> =
  { xxs: "xxs", xs: "xs", sm: "xs", md: "sm", lg: "md" };
